/**
 * ConnectorManager - Handles connector creation, editing, and synchronization
 * Implements Figma-style connector tool behavior
 */

import Konva from 'konva';
import { EventEmitter } from '../core/EventEmitter';
import { Point } from '../core/types';
import { 
  EditableElement, 
  ConnectorElement, 
  ConnectorData, 
  ConnectorPathType, 
  AnchorPoint,
  ConnectorArrowStyle,
} from '../editing/types';
import { 
  findElementAtPoint, 
  snapToAnchor, 
  calculatePreciseAnchor, 
  findBestAnchor,
  findNearbyShape,
  findNearestAnchorOnShape,
  calculateAnchorPoints,
  getEdgeAnchorFromOffsetAnchor,
} from '../utils/connectorUtils';
import { calculatePathPoints } from '../utils/connectorPathUtils';
import { createConnector, createShapeFromAnchor } from '../editing/ElementFactory';

export interface ConnectorToolState {
  mode: 'idle' | 'dragging-from-shape' | 'dragging-from-empty';
  startElementId: string | null;
  startAnchor: AnchorPoint | null;
  currentAnchor: AnchorPoint | null;
  hoveredElementId: string | null;
  previewPath: number[];
}

export interface ConnectorManagerOptions {
  pathType?: ConnectorPathType;
  stroke?: string;
  strokeWidth?: 'small' | 'medium' | 'large' | 'xlarge';
  dashEnabled?: boolean;
  arrowStart?: ConnectorArrowStyle;
  arrowEnd?: ConnectorArrowStyle;
}

const DEFAULT_OPTIONS: Required<ConnectorManagerOptions> = {
  pathType: 'curved',
  stroke: '#6B7280',
  strokeWidth: 'medium',
  dashEnabled: false,
  arrowStart: 'none',
  arrowEnd: 'solid',
};

export class ConnectorManager {
  private layer: Konva.Layer;
  private emitter: EventEmitter;
  private options: Required<ConnectorManagerOptions>;
  
  // Elements map
  private elements: Map<string, EditableElement> = new Map();
  
  // Tool state
  private toolState: ConnectorToolState = {
    mode: 'idle',
    startElementId: null,
    startAnchor: null,
    currentAnchor: null,
    hoveredElementId: null,
    previewPath: [],
  };
  
  // Preview line
  private previewLine: Konva.Line | Konva.Shape | null = null;
  
  // Connector mode (null = not in connector mode)
  private connectorMode: ConnectorPathType | null = null;
  
  // Callbacks
  private onConnectorCreated: ((connector: ConnectorElement, newShape?: EditableElement) => void) | null = null;
  private onControlPointUpdate: ((connectorId: string, index: number, x: number, y: number) => void) | null = null;

  constructor(
    layer: Konva.Layer,
    emitter: EventEmitter,
    options: ConnectorManagerOptions = {}
  ) {
    this.layer = layer;
    this.emitter = emitter;
    this.options = { ...DEFAULT_OPTIONS, ...options };
  }

  /**
   * Set elements map
   */
  setElements(elements: EditableElement[]): void {
    this.elements.clear();
    elements.forEach((el) => {
      if (el.type !== 'connector') {
        this.elements.set(el.id, el);
      }
    });
  }

  /**
   * Set connector mode
   */
  setConnectorMode(mode: ConnectorPathType | null): void {
    this.connectorMode = mode;
    if (mode) {
      this.options.pathType = mode;
    }
    if (!mode) {
      this.cancel();
    }
  }

  /**
   * Get connector mode
   */
  getConnectorMode(): ConnectorPathType | null {
    return this.connectorMode;
  }

  /**
   * Check if in connector mode
   */
  isInConnectorMode(): boolean {
    return this.connectorMode !== null;
  }

  /**
   * Check if actively creating a connector
   */
  isActive(): boolean {
    return this.toolState.mode !== 'idle';
  }

  /**
   * Set callbacks
   */
  setOnConnectorCreated(callback: (connector: ConnectorElement, newShape?: EditableElement) => void): void {
    this.onConnectorCreated = callback;
  }

  setOnControlPointUpdate(callback: (connectorId: string, index: number, x: number, y: number) => void): void {
    this.onControlPointUpdate = callback;
  }

  /**
   * Start connector creation from a shape
   */
  startFromShape(elementId: string, pointerPos: Point, isPrecise: boolean = false): void {
    const element = this.elements.get(elementId);
    if (!element) return;
    
    let anchor: AnchorPoint;
    if (isPrecise) {
      anchor = calculatePreciseAnchor(pointerPos, element);
    } else {
      const snapped = snapToAnchor(pointerPos, element, 30);
      anchor = snapped || calculatePreciseAnchor(pointerPos, element);
    }
    
    this.toolState = {
      mode: 'dragging-from-shape',
      startElementId: elementId,
      startAnchor: anchor,
      currentAnchor: anchor,
      hoveredElementId: null,
      previewPath: [anchor.x, anchor.y, anchor.x, anchor.y],
    };
    
    this.updatePreviewLine();
  }

  /**
   * Start connector creation from empty canvas
   */
  startFromEmpty(pointerPos: Point): void {
    const anchor: AnchorPoint = {
      side: 'center',
      x: pointerPos.x,
      y: pointerPos.y,
      isPrecise: true,
    };
    
    this.toolState = {
      mode: 'dragging-from-empty',
      startElementId: null,
      startAnchor: anchor,
      currentAnchor: anchor,
      hoveredElementId: null,
      previewPath: [anchor.x, anchor.y, anchor.x, anchor.y],
    };
    
    this.updatePreviewLine();
  }

  /**
   * Update connector preview during drag
   */
  updatePreview(pointerPos: Point, isPrecise: boolean = false): void {
    if (this.toolState.mode === 'idle') return;
    
    let currentAnchor: AnchorPoint;
    let hoveredElementId: string | null = null;
    
    // Check if hovering over an element
    const elements = Array.from(this.elements.values());
    const hoveredElement = findElementAtPoint(pointerPos, elements);
    
    if (hoveredElement && hoveredElement.id !== this.toolState.startElementId) {
      hoveredElementId = hoveredElement.id;
      
      if (isPrecise) {
        currentAnchor = calculatePreciseAnchor(pointerPos, hoveredElement);
      } else {
        const snapped = snapToAnchor(pointerPos, hoveredElement, 30);
        currentAnchor = snapped || calculatePreciseAnchor(pointerPos, hoveredElement);
      }
    } else {
      // Not hovering over an element - use pointer position
      currentAnchor = {
        side: 'center',
        x: pointerPos.x,
        y: pointerPos.y,
        isPrecise: true,
      };
    }
    
    // Calculate preview path based on path type
    const startAnchor = this.toolState.startAnchor || currentAnchor;
    const previewPath = calculatePathPoints(this.options.pathType, startAnchor, currentAnchor);
    
    this.toolState = {
      ...this.toolState,
      currentAnchor,
      hoveredElementId,
      previewPath,
    };
    
    this.updatePreviewLine();
  }

  /**
   * Finish connector creation
   */
  finish(): { connector: ConnectorElement; newShape?: EditableElement } | null {
    if (this.toolState.mode === 'idle' || !this.toolState.startAnchor || !this.toolState.currentAnchor) {
      return null;
    }
    
    const elements = Array.from(this.elements.values());
    const startElement = this.toolState.startElementId
      ? this.elements.get(this.toolState.startElementId)
      : null;
    const endElement = this.toolState.hoveredElementId
      ? this.elements.get(this.toolState.hoveredElementId)
      : null;
    
    // If we have both elements, use best anchor for end
    let endAnchor = this.toolState.currentAnchor;
    if (startElement && endElement) {
      endAnchor = findBestAnchor(startElement, endElement, this.toolState.startAnchor);
    }
    
    let newShape: EditableElement | undefined;
    let targetEndElement = endElement;
    let targetEndAnchor = endAnchor;
    
    // If no target element, create a new shape
    if (!targetEndElement && startElement) {
      const result = createShapeFromAnchor(startElement, this.toolState.startAnchor, 100);
      newShape = result.shape;
      targetEndAnchor = result.targetAnchor;
    }
    
    // Create the connector
    const connector = createConnector(
      this.toolState.startAnchor,
      targetEndAnchor,
      {
        pathType: this.options.pathType,
        startElementId: this.toolState.startElementId,
        endElementId: targetEndElement?.id || newShape?.id || null,
        stroke: this.options.stroke,
        strokeWidth: this.options.strokeWidth,
        dashEnabled: this.options.dashEnabled,
        arrowStart: this.options.arrowStart as 'none' | 'solid' | 'line' | 'triangle' | 'diamond',
        arrowEnd: this.options.arrowEnd as 'none' | 'solid' | 'line' | 'triangle' | 'diamond',
      }
    );
    
    // Reset tool state
    this.reset();
    
    // Notify callback
    if (this.onConnectorCreated) {
      this.onConnectorCreated(connector, newShape);
    }
    
    return { connector, newShape };
  }

  /**
   * Handle anchor point click (quick connect)
   */
  handleAnchorClick(anchor: AnchorPoint, element: EditableElement, anchorOffset: number = 18): void {
    // Convert offset anchor to edge position
    const edgeAnchor = getEdgeAnchorFromOffsetAnchor(anchor, element, anchorOffset);
    
    const elements = Array.from(this.elements.values());
    const nearbyShape = findNearbyShape(edgeAnchor, element.id, elements, 100);
    
    if (nearbyShape) {
      // Connect to nearby shape
      const targetAnchor = findBestAnchor(element, nearbyShape, edgeAnchor);
      const connector = createConnector(
        edgeAnchor,
        targetAnchor,
        {
          pathType: this.options.pathType,
          startElementId: element.id,
          endElementId: nearbyShape.id,
          stroke: this.options.stroke,
          strokeWidth: this.options.strokeWidth,
        }
      );
      
      if (this.onConnectorCreated) {
        this.onConnectorCreated(connector);
      }
    } else {
      // Create new shape and connect
      const { shape, targetAnchor } = createShapeFromAnchor(element, edgeAnchor, 100);
      const connector = createConnector(
        edgeAnchor,
        targetAnchor,
        {
          pathType: this.options.pathType,
          startElementId: element.id,
          endElementId: shape.id,
          stroke: this.options.stroke,
          strokeWidth: this.options.strokeWidth,
        }
      );
      
      if (this.onConnectorCreated) {
        this.onConnectorCreated(connector, shape);
      }
    }
  }

  /**
   * Handle anchor drag start
   */
  handleAnchorDragStart(anchor: AnchorPoint, element: EditableElement, anchorOffset: number = 18): void {
    const edgeAnchor = getEdgeAnchorFromOffsetAnchor(anchor, element, anchorOffset);
    this.startFromShape(element.id, { x: edgeAnchor.x, y: edgeAnchor.y }, false);
  }

  /**
   * Handle anchor drag end
   */
  handleAnchorDragEnd(anchor: AnchorPoint, element: EditableElement, pointerPos: Point, anchorOffset: number = 18): { connector: ConnectorElement; newShape?: EditableElement } | null {
    if (this.toolState.mode === 'idle') return null;
    
    // Update to final position
    this.updatePreview(pointerPos, false);
    
    // Finish and create connector
    return this.finish();
  }

  /**
   * Cancel connector creation
   */
  cancel(): void {
    this.reset();
  }

  /**
   * Reset tool state
   */
  private reset(): void {
    this.toolState = {
      mode: 'idle',
      startElementId: null,
      startAnchor: null,
      currentAnchor: null,
      hoveredElementId: null,
      previewPath: [],
    };
    this.clearPreviewLine();
  }

  /**
   * Update preview line visualization
   */
  private updatePreviewLine(): void {
    this.clearPreviewLine();
    
    if (this.toolState.previewPath.length < 4) return;
    
    if (this.options.pathType === 'curved') {
      this.previewLine = new Konva.Shape({
        stroke: '#0066FF',
        strokeWidth: 2,
        dash: [5, 5],
        lineCap: 'round',
        lineJoin: 'round',
        listening: false,
        sceneFunc: (context, shape) => {
          const path = this.toolState.previewPath;
          context.beginPath();
          context.moveTo(path[0], path[1]);
          if (path.length >= 8) {
            context.bezierCurveTo(
              path[2], path[3],
              path[4], path[5],
              path[6], path[7]
            );
          } else {
            context.lineTo(path[path.length - 2], path[path.length - 1]);
          }
          context.lineCap = 'round';
          context.lineJoin = 'round';
          context.stroke();
          context.fillStrokeShape(shape);
        },
      });
    } else {
      this.previewLine = new Konva.Line({
        points: this.toolState.previewPath,
        stroke: '#0066FF',
        strokeWidth: 2,
        dash: [5, 5],
        lineCap: 'round',
        lineJoin: 'round',
        listening: false,
      });
    }
    
    this.layer.add(this.previewLine);
    this.previewLine.moveToTop();
    this.layer.batchDraw();
  }

  /**
   * Clear preview line
   */
  private clearPreviewLine(): void {
    if (this.previewLine) {
      this.previewLine.destroy();
      this.previewLine = null;
      this.layer.batchDraw();
    }
  }

  /**
   * Get preview path for external rendering
   */
  getPreviewPath(): number[] {
    return [...this.toolState.previewPath];
  }

  /**
   * Get hovered element ID
   */
  getHoveredElementId(): string | null {
    return this.toolState.hoveredElementId;
  }

  /**
   * Get current options
   */
  getOptions(): Required<ConnectorManagerOptions> {
    return { ...this.options };
  }

  /**
   * Set options
   */
  setOptions(options: Partial<ConnectorManagerOptions>): void {
    this.options = { ...this.options, ...options };
  }

  /**
   * Sync connectors with moved elements
   */
  syncConnectorsWithElements(
    connectors: ConnectorElement[],
    allElements: EditableElement[],
    movedElementId: string
  ): ConnectorElement[] {
    // Import sync function and use it
    const { syncConnectorsWithElements } = require('../utils/connectorSync');
    return syncConnectorsWithElements(connectors, allElements, movedElementId);
  }

  /**
   * Destroy the connector manager
   */
  destroy(): void {
    this.clearPreviewLine();
    this.elements.clear();
    this.onConnectorCreated = null;
    this.onControlPointUpdate = null;
  }
}
